// ============================================================
// Moteur PUR des disponibilités déclarées — AUCUNE dépendance Angular.
// Projette les règles (récurrentes ou ponctuelles) sur des jours réels.
// Testé sans TestBed : hr-availability.spec.ts.
// ============================================================
//
// Le besoin, mot pour mot en démo : « Radia, tous les mercredis après-midi, elle s'occupe de ses
// enfants — je ne peux jamais lui demander. » On veut le voir AVANT de construire le planning,
// pas le découvrir en le publiant.
//
// PRINCIPE — informatif, JAMAIS bloquant. Comme les garde-fous légaux, on avertit le
// gestionnaire ; il reste libre de planifier quelqu'un sur son indisponibilité (un remplacement
// d'urgence se négocie, il ne se refuse pas par un logiciel).

import { HrAvailability } from '@app/shared/_models/hr';
import { HR_AVAILABILITY_AVAILABLE, HR_AVAILABILITY_UNAVAILABLE } from '@app/shared/constants';
import { isoWeekdayOf, minutesLabel } from './hr-schedule-engine';

// Une règle projetée sur un jour donné.
export interface AvailabilityMark {
	id: number;
	id_employee: number;
	dat: string;
	kind: number;								// HR_AVAILABILITY_*
	start_min: number | null;		// null = journée entière
	end_min: number | null;
	comment: string;
	recurring: boolean;
}

/// La règle s'applique-t-elle à ce jour ?
/// Récurrente : le jour de semaine correspond ET la date est dans les bornes de validité.
/// Ponctuelle : la date correspond exactement.
export function availabilityAppliesOn(a: HrAvailability, dat: string): boolean {
	if (!dat) return false;
	if (a.dat) return a.dat === dat;
	if (a.weekday == null) return false;
	if (isoWeekdayOf(dat) !== a.weekday) return false;
	if (a.datstart && dat < a.datstart) return false;
	if (a.datend && dat > a.datend) return false;
	return true;
}

/// Projette toutes les règles sur [from, to] (bornes incluses).
export function availabilityMarksForRange(
	availabilities: HrAvailability[],
	from: string,
	to: string,
	days: string[],
): AvailabilityMark[] {
	const out: AvailabilityMark[] = [];
	if (!from || !to || to < from) return out;

	for (const dat of days) {
		if (dat < from || dat > to) continue;
		for (const a of availabilities) {
			if (!availabilityAppliesOn(a, dat)) continue;
			out.push({
				id: a.id,
				id_employee: a.id_employee,
				dat,
				kind: a.kind,
				start_min: a.start_min,
				end_min: a.end_min,
				comment: a.comment ?? '',
				recurring: a.dat == null,
			});
		}
	}
	return out;
}

/// Un créneau planifié tombe-t-il sur une INDISPONIBILITÉ déclarée ?
/// Une indisponibilité « journée entière » (bornes nulles) couvre tout créneau du jour.
export function conflictsWithUnavailability(
	marks: AvailabilityMark[],
	id_employee: number,
	dat: string,
	start_min: number,
	end_min: number,
): AvailabilityMark[] {
	return marks.filter(m =>
		m.kind === HR_AVAILABILITY_UNAVAILABLE
		&& m.id_employee === id_employee
		&& m.dat === dat
		&& (m.start_min == null || m.end_min == null || (m.start_min < end_min && start_min < m.end_min)),
	);
}

/// Le repère affiché dans le coin d'une cellule de planning :
/// triangle VERT = la personne s'est déclarée disponible, ROUGE = indisponible.
/// null quand rien n'est déclaré ce jour-là.
export function availabilityBadgeOf(
	marks: AvailabilityMark[],
	id_employee: number,
	dat: string,
): { kind: number; tooltip: string } | null {
	const mine = marks.filter(m => m.id_employee === id_employee && m.dat === dat);
	if (!mine.length) return null;

	// L'indisponibilité prime : c'est elle qui doit sauter aux yeux.
	const unavailable = mine.filter(m => m.kind === HR_AVAILABILITY_UNAVAILABLE);
	const shown = unavailable.length ? unavailable : mine;
	const kind = unavailable.length ? HR_AVAILABILITY_UNAVAILABLE : HR_AVAILABILITY_AVAILABLE;

	const lines = shown.map(m => {
		const when = (m.start_min == null || m.end_min == null)
			? 'toute la journée'
			: minutesLabel(m.start_min) + '–' + minutesLabel(m.end_min);
		const what = m.kind === HR_AVAILABILITY_UNAVAILABLE ? 'Indisponible' : 'Disponible';
		return what + ' ' + when + (m.recurring ? ' (chaque semaine)' : '') + (m.comment ? ' — ' + m.comment : '');
	});
	return { kind, tooltip: lines.join('\n') };
}

/// Libellé d'une règle pour la liste de la fiche employé.
/// « Indisponible tous les mercredis 14:00–18:00 — garde des enfants »
export function availabilityLabel(a: HrAvailability, dayLabels: string[]): string {
	const what = a.kind === HR_AVAILABILITY_UNAVAILABLE ? 'Indisponible' : 'Disponible';
	const when = (a.start_min == null || a.end_min == null)
		? 'toute la journée'
		: minutesLabel(a.start_min) + '–' + minutesLabel(a.end_min);

	let scope: string;
	if (a.dat) {
		scope = 'le ' + a.dat.slice(8, 10) + '/' + a.dat.slice(5, 7) + '/' + a.dat.slice(0, 4);
	} else {
		const day = a.weekday != null ? (dayLabels[a.weekday - 1] ?? '') : '';
		scope = 'tous les ' + day.toLowerCase() + 's';
		if (a.datend) scope += ' jusqu\'au ' + a.datend.slice(8, 10) + '/' + a.datend.slice(5, 7) + '/' + a.datend.slice(0, 4);
	}

	return what + ' ' + scope + ' ' + when + (a.comment ? ' — ' + a.comment : '');
}
